import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FileSearch,
    Shield,
    FileText,
    ExternalLink,
    RefreshCw,
    Search,
    Download,
    Filter,
    X,
    Copy,
    Check,
    AlertTriangle,
    CheckCircle,
    Mail,
    Share2,
    HardDrive,
    Users,
    KeyRound,
    Lock,
    Eye,
    Globe,
    Layers,
    SlidersHorizontal,
    Activity
} from 'lucide-react';
import { backendURL } from '../config';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

export default function PurviewAuditExplorer() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [logs, setLogs] = useState([]);
    const [kpis, setKpis] = useState({});
    
    // Filters
    const [selectedWorkload, setSelectedWorkload] = useState('all');
    const [selectedOperation, setSelectedOperation] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [userFilter, setUserFilter] = useState('');
    
    // Selected record for Purview slide-out drawer
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [copied, setCopied] = useState(false);

    const fetchLogs = async () => {
        setRefreshing(true);
        try {
            const params = new URLSearchParams();
            if (selectedWorkload !== 'all') params.append('workload', selectedWorkload);
            if (selectedOperation !== 'all') params.append('operation', selectedOperation);
            if (searchQuery) params.append('search', searchQuery);
            if (userFilter) params.append('user', userFilter);

            const res = await axios.get(`${backendURL}/api/soc/purview/audit-logs?${params.toString()}`);
            if (res.data && res.data.success) {
                setLogs(res.data.logs || []);
                setKpis(res.data.kpis || {});
            }
        } catch (err) {
            console.error("Purview audit fetch error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [selectedWorkload, selectedOperation]);

    const handleCopyJson = () => {
        if (!selectedRecord) return;
        navigator.clipboard.writeText(JSON.stringify(selectedRecord, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const handleExportCsv = () => {
        if (!logs.length) return;
        const headers = ["Timestamp", "User", "Workload", "Operation", "Target Resource", "Client IP", "Result Status", "Severity"];
        const rows = logs.map(l => [
            `"${l.creationTime}"`,
            `"${l.user}"`,
            `"${l.workload}"`,
            `"${l.operation}"`,
            `"${(l.targetResource || '').replace(/"/g, '""')}"`,
            `"${l.clientIp}"`,
            `"${l.resultStatus}"`,
            `"${l.severity}"`
        ]);
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Purview_Unified_Audit_Export_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getWorkloadMeta = (workload) => {
        switch (workload) {
            case 'Exchange':
                return { label: 'Exchange Online', color: 'bg-[#0078D4]/20 text-[#2899f5] border-[#0078D4]/40', icon: Mail };
            case 'SharePoint':
                return { label: 'SharePoint Online', color: 'bg-[#008272]/20 text-[#00b29a] border-[#008272]/40', icon: Share2 };
            case 'OneDrive':
                return { label: 'OneDrive for Business', color: 'bg-sky-500/20 text-sky-400 border-sky-500/40', icon: HardDrive };
            case 'MicrosoftTeams':
                return { label: 'Microsoft Teams', color: 'bg-[#6264A7]/20 text-[#8b8dc8] border-[#6264A7]/40', icon: Users };
            case 'AzureActiveDirectory':
                return { label: 'Microsoft Entra ID', color: 'bg-purple-500/20 text-purple-400 border-purple-500/40', icon: KeyRound };
            case 'DataLossPrevention':
                return { label: 'Purview DLP Policy', color: 'bg-amber-500/20 text-amber-400 border-amber-500/40', icon: Shield };
            default:
                return { label: workload || 'General', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30', icon: Layers };
        }
    };

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Purview Top Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-teal-600/10 border border-teal-500/20 text-teal-400">
                        <FileSearch className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                Microsoft Purview Audit Log Search
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-500/20 text-teal-400 border border-teal-500/30 font-mono">
                                UNIFIED AUDIT LOG (UAL)
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                                180-Day Forensic Retention
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Compliance Forensic Ledger: Exchange Online, SharePoint, OneDrive, Microsoft Teams, Entra ID &amp; DLP Violations
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={handleExportCsv}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <Download className="w-3.5 h-3.5 text-teal-400" />
                        <span>Export Audit (CSV)</span>
                    </button>
                    <button
                        onClick={fetchLogs}
                        disabled={refreshing}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-teal-400 ${refreshing ? 'animate-spin' : ''}`} />
                        <span>{refreshing ? 'Syncing...' : 'Sync Purview'}</span>
                    </button>
                    <a
                        href="https://compliance.microsoft.com/auditlogsearch"
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-teal-600/20"
                    >
                        <span>Open Purview Portal</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3.5">
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Audit Records
                        <FileText className="w-3.5 h-3.5 text-teal-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-slate-100">
                        {kpis.totalAuditRecords ?? 0}
                    </div>
                    <span className="text-[10px] text-teal-400/80 font-mono mt-1">{kpis.filteredCount ?? 0} Displayed</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Workloads
                        <Layers className="w-3.5 h-3.5 text-blue-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-blue-400">
                        6
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono mt-1">M365 Services Ingested</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        High Risk Events
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-rose-400">
                        {kpis.highRiskComplianceEvents ?? 0}
                    </div>
                    <span className="text-[10px] text-rose-400/80 font-mono mt-1">BEC Rules &amp; Leaks</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        DLP Violations
                        <Shield className="w-3.5 h-3.5 text-amber-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-amber-400">
                        {kpis.dlpViolationsCount ?? 0}
                    </div>
                    <span className="text-[10px] text-amber-400/80 font-mono mt-1">Sensitive Data Blocked</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Audited Users
                        <Users className="w-3.5 h-3.5 text-purple-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-purple-300">
                        {kpis.uniqueUsersCount ?? 0}
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono mt-1">Active Identities</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                        Directory Mutations
                        <KeyRound className="w-3.5 h-3.5 text-sky-400" />
                    </span>
                    <div className="mt-2 text-2xl font-black font-mono text-sky-400">
                        {kpis.privilegedAdminActions ?? 0}
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono mt-1">Admin Security Actions</span>
                </div>
            </div>

            {/* Purview Audit Filter Console */}
            <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-3">
                <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                        <SlidersHorizontal className="w-3.5 h-3.5 text-teal-400" />
                        <span>Unified Audit Log (UAL) Search Parameters</span>
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">Microsoft Compliance Schema v2.4</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Workload Filter */}
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-slate-400 font-mono">
                            Workload / M365 Service
                        </label>
                        <select
                            value={selectedWorkload}
                            onChange={(e) => setSelectedWorkload(e.target.value)}
                            className="w-full bg-[#070b14] border border-white/[0.1] rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500 font-mono"
                        >
                            <option value="all">All Workloads (Universal UAL)</option>
                            <option value="Exchange">Exchange Online (Mailbox &amp; Rules)</option>
                            <option value="SharePoint">SharePoint Online (Documents &amp; Sharing)</option>
                            <option value="OneDrive">OneDrive for Business (Personal Sync)</option>
                            <option value="MicrosoftTeams">Microsoft Teams (Channels &amp; Guests)</option>
                            <option value="AzureActiveDirectory">Microsoft Entra ID (Identity &amp; Admin)</option>
                            <option value="DataLossPrevention">Purview DLP (Sensitive Data Policy)</option>
                        </select>
                    </div>

                    {/* Operation Filter */}
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-slate-400 font-mono">
                            Operation / Activity
                        </label>
                        <select
                            value={selectedOperation}
                            onChange={(e) => setSelectedOperation(e.target.value)}
                            className="w-full bg-[#070b14] border border-white/[0.1] rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-teal-500 font-mono"
                        >
                            <option value="all">All Operations</option>
                            <option value="FileDownloaded">FileDownloaded (SharePoint/OneDrive)</option>
                            <option value="New-InboxRule">New-InboxRule (Auto-Forwarding Rule)</option>
                            <option value="Add-MailboxPermission">Add-MailboxPermission (Delegate Rights)</option>
                            <option value="SharingInvitationCreated">SharingInvitationCreated (External Link)</option>
                            <option value="UserLoggedIn">UserLoggedIn (Entra Interactive Login)</option>
                            <option value="DLPPolicyMatch">DLPPolicyMatch (Data Loss Prevention)</option>
                        </select>
                    </div>

                    {/* User UPN Search */}
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-slate-400 font-mono">
                            User (UPN / Actor)
                        </label>
                        <input
                            type="text"
                            placeholder="e.g. aftab.akhtar@querytel.com"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                            className="w-full bg-[#070b14] border border-white/[0.1] rounded-xl px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500 font-mono"
                        />
                    </div>

                    {/* Keyword / Target Search */}
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-slate-400 font-mono">
                            Keyword / File / Target IP
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Search keyword or IP..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                                className="w-full bg-[#070b14] border border-white/[0.1] rounded-xl px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-teal-500 font-mono"
                            />
                            <button
                                onClick={fetchLogs}
                                className="px-3.5 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs font-mono transition-all shrink-0"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Audit Log Data Table */}
            <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden space-y-3">
                <div className="p-4 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/40">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono">
                            Purview Audit Stream Records
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/[0.06] text-slate-400 font-mono">
                            {logs.length} Events
                        </span>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400">
                        Click any row to inspect full JSON payload
                    </span>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                        <thead>
                            <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                <th className="px-4 py-3">Date (UTC)</th>
                                <th className="px-4 py-3">User (UPN)</th>
                                <th className="px-4 py-3">Workload</th>
                                <th className="px-4 py-3">Operation / Event</th>
                                <th className="px-4 py-3">Target Resource / Object</th>
                                <th className="px-4 py-3">Client IP (AbuseIPDB &amp; VT)</th>
                                <th className="px-4 py-3 text-right">Result Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {logs.map((item, idx) => {
                                const wMeta = getWorkloadMeta(item.workload);
                                const Icon = wMeta.icon;
                                return (
                                    <tr
                                        key={idx}
                                        onClick={() => setSelectedRecord(item)}
                                        className="hover:bg-white/[0.03] transition-colors cursor-pointer group"
                                    >
                                        <td className="px-4 py-3 font-mono text-[11px] text-slate-400 whitespace-nowrap align-top">
                                            {item.creationTime ? new Date(item.creationTime).toLocaleString() : 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <span className="font-semibold text-slate-200 group-hover:text-teal-300 transition-colors block">
                                                {item.user}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-mono">
                                                Tenant Member
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap align-top">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border inline-flex items-center gap-1.5 ${wMeta.color}`}>
                                                <Icon className="w-3 h-3" />
                                                <span>{wMeta.label}</span>
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-top max-w-md">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`px-1.5 py-0.2 rounded text-[11.5px] font-bold ${
                                                    item.severity === 'Critical' ? 'text-rose-400 bg-rose-500/10' :
                                                    item.severity === 'High' ? 'text-orange-400 bg-orange-500/10' :
                                                    item.severity === 'Medium' ? 'text-amber-400 bg-amber-500/10' : 'text-slate-100'
                                                }`}>
                                                    {item.operation}
                                                </span>
                                                {item.rawOperation && item.rawOperation !== item.operation && (
                                                    <span className="text-[9px] font-mono text-slate-500 bg-black/40 px-1.5 py-0.5 rounded border border-white/[0.04]">
                                                        {item.rawOperation}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Human-Readable "What Happened / What User Did" Explanation */}
                                            <p className="text-[11px] text-slate-300 font-sans font-normal mt-1 leading-snug">
                                                {item.whatUserDid || item.details}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 text-slate-300 font-mono text-[11px] max-w-[200px] truncate align-top" title={item.targetResource}>
                                            {item.targetResource}
                                        </td>
                                        <td className="px-4 py-3 font-mono align-top" onClick={(e) => e.stopPropagation()}>
                                            <AbuseIPDBPopover ip={item.clientIp} showBadge={true} className="text-blue-400 hover:underline">
                                                {item.clientIp}
                                            </AbuseIPDBPopover>
                                        </td>
                                        <td className="px-4 py-3 text-right align-top">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase font-mono ${
                                                item.resultStatus === 'Blocked' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                                item.resultStatus === 'Flagged' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                                                item.resultStatus === 'Failed' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                                'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                            }`}>
                                                {item.resultStatus}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Slide-Out Purview Audit Item Inspection Drawer */}
            {selectedRecord && (
                <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-xl bg-[#0d1424] border-l border-white/[0.1] h-full overflow-y-auto p-6 space-y-6 shadow-2xl flex flex-col justify-between">
                        <div className="space-y-5">
                            {/* Drawer Header */}
                            <div className="flex items-center justify-between border-b border-white/[0.08] pb-4">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-500/30">
                                        <FileSearch className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-black text-slate-100 uppercase tracking-wide font-mono">
                                            Purview Audit Item Details
                                        </h2>
                                        <span className="text-[10px] text-slate-400 font-mono">
                                            ID: {selectedRecord.id}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setSelectedRecord(null)}
                                    className="p-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-all"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Human-Readable Explanation Cards */}
                            <div className="p-3.5 rounded-xl bg-teal-950/20 border border-teal-500/25 space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400 font-mono flex items-center gap-1.5">
                                    <Users className="w-3.5 h-3.5" />
                                    <span>What Did The User Do?</span>
                                </span>
                                <p className="text-xs text-slate-100 leading-relaxed font-sans font-medium">
                                    {selectedRecord.whatUserDid || selectedRecord.details}
                                </p>
                            </div>

                            <div className="p-3.5 rounded-xl bg-blue-950/20 border border-blue-500/25 space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 font-mono flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" />
                                    <span>What Is This Log? (Technical Purpose)</span>
                                </span>
                                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                                    {selectedRecord.whatIsThisLog || "Audited operation recorded by Microsoft Purview Unified Audit Log."}
                                </p>
                            </div>

                            {/* Full Narrative */}
                            <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.06] space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 font-mono flex items-center gap-1.5">
                                    <Eye className="w-3.5 h-3.5" />
                                    <span>Purview Forensic Narrative</span>
                                </span>
                                <p className="text-xs text-slate-300 leading-relaxed font-sans">
                                    {selectedRecord.details}
                                </p>
                            </div>

                            {/* Summary Metadata Grid */}
                            <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                                <div className="p-3 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Workload</span>
                                    <span className="font-bold text-teal-300">{selectedRecord.workload}</span>
                                </div>
                                <div className="p-3 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Operation</span>
                                    <span className="font-bold text-slate-100">{selectedRecord.operation}</span>
                                </div>
                                <div className="p-3 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Actor (User)</span>
                                    <span className="font-bold text-slate-100 break-all">{selectedRecord.user}</span>
                                </div>
                                <div className="p-3 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Timestamp (UTC)</span>
                                    <span className="text-slate-300">{selectedRecord.creationTime}</span>
                                </div>
                            </div>

                            {/* Description / Narrative */}
                            <div className="p-3.5 rounded-xl bg-purple-950/20 border border-purple-500/20 space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 font-mono flex items-center gap-1.5">
                                    <Eye className="w-3.5 h-3.5" />
                                    <span>Purview Forensic Narrative</span>
                                </span>
                                <p className="text-xs text-slate-200 leading-relaxed font-sans">
                                    {selectedRecord.details}
                                </p>
                            </div>

                            {/* Target Object */}
                            <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                <span className="text-[10px] uppercase text-slate-500 font-mono block">Target Resource / Path</span>
                                <span className="text-xs font-mono text-slate-200 break-all">{selectedRecord.targetResource}</span>
                            </div>

                            {/* Client IP & Reputation */}
                            <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] uppercase text-slate-500 font-mono block">Origin Client IP</span>
                                    <span className="text-xs font-mono text-slate-200">{selectedRecord.clientIp}</span>
                                </div>
                                <AbuseIPDBPopover ip={selectedRecord.clientIp} showBadge={true} className="text-blue-400 hover:underline">
                                    Check IP Reputation
                                </AbuseIPDBPopover>
                            </div>

                            {/* Raw Audit JSON Viewer */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                                        Raw Audit Event JSON
                                    </span>
                                    <button
                                        onClick={handleCopyJson}
                                        className="px-2.5 py-1 rounded bg-white/[0.05] hover:bg-white/[0.1] text-[10px] font-mono text-teal-300 transition-all flex items-center gap-1"
                                    >
                                        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                                        <span>{copied ? 'Copied' : 'Copy JSON'}</span>
                                    </button>
                                </div>
                                <pre className="p-3.5 rounded-xl bg-[#040711] border border-white/[0.06] text-[11px] font-mono text-slate-300 overflow-x-auto max-h-56 leading-relaxed">
                                    {JSON.stringify(selectedRecord, null, 2)}
                                </pre>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-white/[0.08]">
                            <button
                                onClick={() => setSelectedRecord(null)}
                                className="w-full py-2 bg-white/[0.08] hover:bg-white/[0.12] text-slate-200 rounded-xl text-xs font-bold font-mono transition-all"
                            >
                                Close Audit Drawer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
